import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ApiError } from '../lib/api'
import { toast } from '../lib/toast'

/** The one place a failed write is reported (MS-819 Q9). A
 * `status_conflict.*` 409 means the server guards refused the write because
 * the product's status moved under a page that still showed the full view
 * (e.g. the daily flip to Expired): it says so and refetches `/quota`, so
 * ProductViewGate re-resolves the tab to the view the new status allows. Any
 * other failure gets the generic "couldn't save" toast. The wording per write
 * belongs to the page tickets (MS-820/821/822); this keeps a refused write
 * from failing silently until then. */
export function useWriteErrorHandler(): (error: unknown) => Promise<void> {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useCallback(
    async (error: unknown) => {
      if (error instanceof ApiError && error.code.startsWith('status_conflict.')) {
        toast(t('common.statusConflict'))
        await queryClient.invalidateQueries({ queryKey: ['quota'] })
        return
      }
      toast(t('common.saveError'))
    },
    [queryClient, t]
  )
}
